// @ts-strict-ignore
import React, { useMemo, useState } from 'react';
import {
  Box, Button, ButtonVariant, Icon, IconName,
} from '@usealloy/component-library';
import {
  selectDataRoutes,
  selectFocusedAction,
} from 'flow/store/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';
import { generatePublicId } from 'flow/service/publicIds';
import { getDynamicDataRoutesForAction } from '~/dashboard/views/workflows/service/dataRoutes';
import DynamicRouteRow from './DynamicRouteRow';
import { StyledDynamicRoutesInfoBanner, StyledDynamicRoutesInfoBannerText } from './styles';

// "Custom Routes" - dynamic (target_payload_key) routes for the open node; only rendered
// when the node has no legacy category routes (see ServiceDataRoutingTab).
export default function DynamicRoutes() {
  const dataRoutes = useAppSelector(selectDataRoutes);
  const focusedAction = useAppSelector(selectFocusedAction);
  const [pendingNewRouteIds, setPendingNewRouteIds] = useState<string[]>([]);

  const dynamicRoutes = useMemo(() => (
    getDynamicDataRoutesForAction({ action: focusedAction, dataRoutes })
  ), [focusedAction, dataRoutes]);

  const onAddRoute = () => {
    setPendingNewRouteIds(current => [...current, generatePublicId('dataroute')]);
  };

  const onRowSettled = (publicId: string) => {
    setPendingNewRouteIds(current => current.filter(id => id !== publicId));
  };

  return (
    <Box direction="column" alignItems="stretch" gap={3}>
      <StyledDynamicRoutesInfoBanner>
        <Icon icon={IconName.Info} size="small" />
        <StyledDynamicRoutesInfoBannerText as="span">
          Data routing takes the output of one service or input/output attribute and feeds it
          into the request payload of a different service within the same Workflow.
        </StyledDynamicRoutesInfoBannerText>
      </StyledDynamicRoutesInfoBanner>

      <Box justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
        <Button
          variant={ButtonVariant.secondary}
          icon={IconName.Add}
          disabled={pendingNewRouteIds.length > 0}
          onClick={onAddRoute}
        >
          Add Data Route
        </Button>
      </Box>

      {dynamicRoutes.map(dataRoute => (
        <DynamicRouteRow
          key={dataRoute.publicId}
          publicId={dataRoute.publicId}
          onSaved={onRowSettled}
          onRemoved={onRowSettled}
        />
      ))}
      {pendingNewRouteIds.map(publicId => (
        <DynamicRouteRow
          key={publicId}
          publicId={publicId}
          onSaved={onRowSettled}
          onRemoved={onRowSettled}
        />
      ))}
    </Box>
  );
}
